import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getMyPackageRoles } from "../../Admin/Packages/api/adminPackageClient";
import { PackageRoleDto } from "../../Admin/Packages/api/models/PackageDto";
import { getToolOwnerTools } from "../../Admin/Tools/api/adminToolManagerClient";
import type { RootState } from "../../App/store/store";

export type HeiUser = {
  oid: string;
  adPrinciple: string;
  name: string;
  initials: string;
  email: string;
  roles: string[];
  isGuest: boolean;
};

export type Account = {
  accountId: string;
  accessToken: string;
};

const initialState: AccountState = {
  accountId: "",
  accessToken: "",
  toolOwnerTools: [],
  packageRoles: [],
  heiUser: {
    oid: "",
    adPrinciple: "",
    initials: "",
    name: "",
    roles: ["User"],
    email: "",
    isGuest: false,
  },
};

export type AccountState = Account & {
  heiUser: HeiUser;
  toolOwnerTools: string[];
  packageRoles: PackageRoleDto[];
};

export const fetchToolOwnerTools = createAsyncThunk(
  "account/fetchToolOwnerTools",
  async () => {
    return await getToolOwnerTools();
  }
);

export const fetchPackageRoles = createAsyncThunk(
  "account/fetchPackageRoles",
  async () => {
    return await getMyPackageRoles();
  }
);

export const accountSlice = createSlice({
  name: "account",
  initialState,
  reducers: {
    setAccountId: (state: AccountState, action: PayloadAction<string>) => {
      state.accountId = action.payload;
    },
    setToken: (state: AccountState, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },
    setHeiUser: (state: AccountState, action: PayloadAction<HeiUser>) => {
      state.heiUser = action.payload;
    },
    setToolOwnerTools: (
      state: AccountState,
      action: PayloadAction<string[]>
    ) => {
      state.toolOwnerTools = action.payload;
    },
    logout: (state: AccountState) => {
      state.accountId = "";
      state.accessToken = "";
      state.toolOwnerTools = [];
      state.packageRoles = [];
      state.heiUser = {
        oid: "",
        adPrinciple: "",
        initials: "",
        name: "",
        roles: ["User"],
        email: "",
        isGuest: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchToolOwnerTools.fulfilled, (state, action) => {
        state.toolOwnerTools = action.payload;
      })
      .addCase(fetchToolOwnerTools.rejected, (state) => {
        state.toolOwnerTools = [];
      })
      .addCase(fetchPackageRoles.fulfilled, (state, action) => {
        state.packageRoles = action.payload;
      })
      .addCase(fetchPackageRoles.rejected, (state) => {
        state.packageRoles = [];
      });
  },
});

export const { setAccountId, setToken, setHeiUser, setToolOwnerTools, logout } =
  accountSlice.actions;

export const selectAccountId = (state: RootState) => state.account.accountId;
export const selectToken = (state: RootState) => state.account.accessToken;
export const selectHeiUser = (state: RootState) => state.account.heiUser;
export const selectToolOwnerTools = (state: RootState) =>
  state.account?.toolOwnerTools ?? [];
export const selectPackageRoles = (state: RootState) =>
  state.account?.packageRoles ?? [];
export const selectHasPavedRoadsAccess = (state: RootState) =>
  selectPackageRoles(state).length > 0;

export const selectCanCreateProject = (state: RootState) => {
  const isAdmin = state.account.heiUser.roles.some((r) => r === "Admin");
  const isGuest = state.account.heiUser.isGuest;
  const isProduction = window.configuration.environment === "prod";
  console.log(
    "isAdmin:",
    isAdmin,
    "isGuest:",
    isGuest,
    "isProduction:",
    isProduction
  );
  return isAdmin || (!isGuest && isProduction) || !isProduction;
};

export default accountSlice.reducer;
